import { useEffect, useRef, useState } from "react";
import { getRolloverPreview } from "../../../contexts/services/rolloverServices";

export default function useRolloverPreview({
  open,
  enabled,
  targetId,
  sourceId,
}) {
  const cache = useRef(new Map());
  const [result, setResult] = useState({});
  const [attempt, setAttempt] = useState(0);
  const key = JSON.stringify([targetId, sourceId]);

  useEffect(() => {
    if (!open) {
      cache.current.clear();
      setResult({});
      return;
    }
    if (!enabled || !sourceId) return;
    if (!targetId) {
      setResult({
        key,
        error:
          "The current tax computation is unavailable. Reopen the computation and try again.",
      });
      return;
    }
    if (cache.current.has(key)) {
      setResult({ key, data: cache.current.get(key) });
      return;
    }

    // Start on source selection in step 1; reuse the result in step 2.
    const controller = new AbortController();
    setResult({ key, loading: true });
    getRolloverPreview(targetId, sourceId, controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return;
        cache.current.set(key, data);
        setResult({ key, data });
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        setResult({
          key,
          error:
            error?.response?.data?.message ||
            error?.message ||
            "Unable to load the rollover preview. Please try again.",
        });
      });
    return () => controller.abort();
  }, [open, enabled, targetId, sourceId, key, attempt]);

  return {
    data: enabled && result.key === key ? result.data : null,
    error: enabled && result.key === key ? result.error : null,
    loading:
      open && enabled && sourceId && (result.key !== key || result.loading),
    retry: () => {
      cache.current.delete(key);
      setResult({ key, loading: true });
      setAttempt((value) => value + 1);
    },
  };
}
